import {
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { ControlState, Purpose, Root } from "../../../../../supabase/functions/sync-control/model";
import { digest, publicJwk, type SignedRequest } from "../../../../../supabase/functions/sync-control/policy";
import { SCHEMA_VERSION } from "../localStore/schema";
import { signChallenge, unbase64url } from "./cryptoV2";
import type { UnlockedDevice } from "./deviceVault";

export type ControlSummary = {
  devices: ControlState["devices"];
  root: Root | null;
  drive: { subject: string; displayEmail: string } | null;
  oauthCandidate: { subject: string; displayEmail: string } | null;
};
export type SigningDevice = Pick<UnlockedDevice, "accountId" | "deviceId" | "signing" | "agreement"> & {
  label: string;
};
const retryableControlConflicts = new Set(["devices", "account-state", "pair-read", "verification-options"]);
export class SyncControlClient {
  private token?: { accessToken: string; expiresAt: number; subject: string };
  private candidateToken?: { accessToken: string; expiresAt: number; subject: string };
  constructor(
    private readonly auth: SupabaseClient,
    private readonly endpoint: string,
    readonly device: SigningDevice,
    private readonly network: typeof fetch = fetch,
  ) {
    const url = new URL(endpoint);
    if (url.protocol !== "https:" && !["localhost", "127.0.0.1"].includes(url.hostname))
      throw new Error("sync-control-origin");
  }
  async publicDevice() {
    return {
      label: this.device.label,
      signing: publicJwk(await crypto.subtle.exportKey("jwk", this.device.signing.publicKey)),
      agreement: publicJwk(await crypto.subtle.exportKey("jwk", this.device.agreement.publicKey)),
    };
  }
  async request<T>(
    action: string,
    payload: Record<string, unknown>,
    retryAuthentication = true,
    conflictAttempt = 0,
  ): Promise<T> {
    const {
      data: { session },
      error,
    } = await this.auth.auth.getSession();
    if (error) throw new Error("sync-control-unavailable");
    if (!session || session.user.id !== this.device.accountId) throw new Error("sync-session-refresh-required");
    const body = {
      action,
      requestId: crypto.randomUUID(),
      issuedAt: Date.now(),
      deviceId: this.device.deviceId,
      payload,
    };
    const request: SignedRequest = {
      ...body,
      signature: await signChallenge(this.device.signing.privateKey, { accountId: this.device.accountId, ...body }),
    };
    let response: Response;
    try {
      response = await this.network.call(globalThis, this.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "",
          "X-Data-Format": String(SCHEMA_VERSION),
        },
        body: JSON.stringify(request),
        signal: AbortSignal.timeout(20000),
        redirect: "error",
        cache: "no-store",
      });
    } catch {
      throw new Error("sync-control-unavailable");
    }
    const value = (await response.json()) as T & { error?: string };
    if (response.status === 401 && retryAuthentication) {
      const refreshed = await this.auth.auth.refreshSession();
      if (refreshed.error)
        throw new Error(refreshed.error.status === 0 ? "sync-control-unavailable" : "sync-sign-in-required");
      if (!refreshed.data.session || refreshed.data.session.user.id !== this.device.accountId)
        throw new Error("sync-sign-in-required");
      return this.request<T>(action, payload, false, conflictAttempt);
    }
    // Reads and registration preparation commit only control state and their
    // replay nonce. An explicit CAS rejection commits neither the proof use nor
    // the new registration challenge. Retry only that rejection with a fresh ID;
    // never replay credential submission, approval or provider side effects.
    if (
      response.status === 409 &&
      value.error === "sync-control-race" &&
      retryableControlConflicts.has(action) &&
      conflictAttempt < 2
    ) {
      await new Promise<void>((resolve) => setTimeout(resolve, 150 * 2 ** conflictAttempt + Math.random() * 150));
      return this.request<T>(action, payload, retryAuthentication, conflictAttempt + 1);
    }
    if (!response.ok)
      throw new Error(
        typeof value.error === "string" && /^sync-[a-z-]+$/.test(value.error)
          ? value.error
          : "sync-control-unavailable",
      );
    const authSession = (value as { authSession?: { access_token: string; refresh_token: string } }).authSession;
    if (authSession) {
      if (
        typeof authSession.access_token !== "string" ||
        typeof authSession.refresh_token !== "string" ||
        JSON.parse(new TextDecoder().decode(unbase64url(authSession.access_token.split(".")[1] ?? "", 32768))).sub !==
          this.device.accountId
      )
        throw new Error("sync-sign-in-required");
      const updated = await this.auth.auth.setSession(authSession);
      if (updated.error || updated.data.user?.id !== this.device.accountId) throw new Error("sync-sign-in-required");
    }
    return value;
  }
  devices() {
    return this.request<ControlSummary>("devices", {});
  }
  async accountState() {
    return this.request<{ root: Root | null; hasVerification: boolean; drive: ControlSummary["drive"] }>(
      "account-state",
      { device: await this.publicDevice() },
    );
  }
  async setupWebAuthn() {
    const device = await this.publicDevice();
    const options = await this.request<{
      challengeId: string;
      options: PublicKeyCredentialCreationOptionsJSON;
      mode: "register" | "authenticate";
    }>("setup-options", { device });
    const response =
      options.mode === "authenticate"
        ? await startAuthentication({
            optionsJSON: options.options as unknown as PublicKeyCredentialRequestOptionsJSON,
          })
        : await startRegistration({ optionsJSON: options.options });
    return this.request("setup-verify", { device, challengeId: options.challengeId, response, mode: options.mode });
  }
  async setupTotp(factorId: string, challengeId: string, code: string) {
    return this.request("setup-totp", { device: await this.publicDevice(), factorId, challengeId, code });
  }
  async stepUp(
    purpose: Purpose,
    target: unknown,
    method: "webauthn" | "totp" = "webauthn",
    totp?: { factorId: string; code: string },
  ) {
    const settings = await this.request<{ challengeId: string; options: PublicKeyCredentialRequestOptionsJSON }>(
      "step-options",
      { purpose, targetHash: await digest(target), method, ...(totp ? { factorId: totp.factorId } : {}) },
    );
    const response = method === "webauthn" ? await startAuthentication({ optionsJSON: settings.options }) : totp?.code;
    if (!response) throw new Error("sync-verification-cancelled");
    return this.request<{ id: string; expiresAt: number }>("step-verify", {
      challengeId: settings.challengeId,
      method,
      response,
    });
  }
  async driveToken(candidate = false) {
    const cached = candidate ? this.candidateToken : this.token;
    if (cached && cached.expiresAt > Date.now() + 60000) return cached.accessToken;
    const result = await this.request<{ accessToken: string; expiresAt: number; subject: string }>(
      "drive-token",
      candidate ? { candidate: true } : {},
    );
    if (!candidate) this.token = result;
    else this.candidateToken = result;
    return result.accessToken;
  }
  clearTransport() {
    this.token = undefined;
    this.candidateToken = undefined;
  }
}
